import { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CreditCard, CheckCircle2, Clock, AlertCircle, XCircle } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { useStripeAccount } from './hooks/useStripeAccount';

const StripeSection = () => {
  const { t } = useI18n();
  const { userRole } = useAuth();

  const {
    status,
    isActive,
    isStandard,
    connecting,
    syncing,
    openingDashboard,
    connect,
    syncStatus,
    completeOAuth,
    openDashboard,
  } = useStripeAccount();

  // Handle Stripe return URL params after onboarding
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const oauthCode = searchParams.get('code');
    const isStripeReturn = searchParams.get('stripe_return') === '1' || searchParams.get('stripe_refresh') === '1';

    if (oauthCode) {
      // OAuth callback: exchange code for account ID
      window.history.replaceState(null, '', '/profile?tab=payment-methods');
      completeOAuth(oauthCode);
    } else if (isStripeReturn) {
      // Express callback: sync status from Stripe
      window.history.replaceState(null, '', '/profile?tab=payment-methods');
      syncStatus();
    }
  }, []);

  // Only show for agency and travel_designer roles (not travel_advisor_member - they use agency's Stripe account)
  if (userRole !== 'agency' && userRole !== 'travel_designer' && userRole !== 'admin') {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl font-normal flex items-center gap-2">
          <CreditCard className="h-4 w-4" />
          {t("agency.stripe.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          {/* Status badge */}
          <div className="flex items-center gap-3">
            {isActive ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    {t("agency.stripe.status.active")}
                  </p>
                  <p className="text-xs text-green-600">
                    {t("agency.stripe.status.active_desc")}
                  </p>
                </div>
              </>
            ) : status === "pending" ? (
              <>
                <Clock className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-yellow-700">
                    {t("agency.stripe.status.pending")}
                  </p>
                  <p className="text-xs text-yellow-600">
                    {t("agency.stripe.status.pending_desc")}
                  </p>
                </div>
              </>
            ) : status === "restricted" ? (
              <>
                <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-orange-700">
                    {t("agency.stripe.status.restricted")}
                  </p>
                  <p className="text-xs text-orange-600">
                    {t("agency.stripe.status.restricted_desc")}
                  </p>
                </div>
              </>
            ) : (
              <>
                <XCircle className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {t("agency.stripe.status.notConnected")}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t("agency.stripe.status.notConnected_desc")}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {isActive ? (
              <Button
                variant="outline"
                size="sm"
                onClick={openDashboard}
                disabled={openingDashboard}
                className="flex items-center gap-1.5"
              >
                {openingDashboard
                  ? t("agency.stripe.openingDashboard")
                  : isStandard
                    ? t("agency.stripe.manageAccountStandard")
                    : t("agency.stripe.manageAccount")}
              </Button>
            ) : (
              <>
                {(status === "pending" || status === "restricted") && (
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => connect('express')}
                      disabled={connecting}
                      className="flex items-center gap-1.5"
                    >
                      {connecting
                        ? t("agency.stripe.onboarding")
                        : t("agency.stripe.continueOnboarding")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={syncStatus}
                      disabled={syncing}
                      className="flex items-center gap-1.5"
                    >
                      {syncing
                        ? t("agency.stripe.verifying")
                        : t("agency.stripe.verifyStatus")}
                    </Button>
                  </div>
                )}
                {status === "not_connected" && (
                  <div className="flex flex-col items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => connect('oauth')}
                      disabled={connecting}
                      className="flex items-center gap-1.5"
                    >
                      {connecting
                        ? t("agency.stripe.onboarding")
                        : t("agency.stripe.connectExisting")}
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => connect('express')}
                      disabled={connecting}
                      className="flex items-center gap-1.5"
                    >
                      {connecting
                        ? t("agency.stripe.onboarding")
                        : t("agency.stripe.connectNew")}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <p className="text-xs text-gray-400 mt-4">
          {t("agency.stripe.paymentNote")}
        </p>
      </CardContent>
    </Card>
  );
};

export default StripeSection;
